import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LOGO_URL } from "../lib/brand";
import {
  LogOut, Inbox, BarChart3, Users, FileText, RefreshCw, Sparkles, Send, Trash2,
  CheckCircle2, ExternalLink, Loader2, AlertCircle, UserPlus, Check, X, Calendar,
  Pencil, Archive, Image as ImageIcon, Download, Upload, Rocket,
} from "lucide-react";
import AdminTopNav from "../components/AdminTopNav";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-CH", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

const SourceBadge = ({ source }) => {
  if (source === "ai") return <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full al-grad-bg text-white">AI</span>;
  if (source === "seed") return <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700">Seed</span>;
  return <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Manual</span>;
};

const StatusBadge = ({ status }) => {
  const s = status || "approved";
  if (s === "pending") return <span data-testid="status-pending" className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Wartet</span>;
  if (s === "rejected") return <span data-testid="status-rejected" className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">Abgelehnt</span>;
  return <span data-testid="status-approved" className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Live</span>;
};

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(null); // post.id while broadcasting/deleting
  const [filter, setFilter] = useState("pending"); // pending | approved | rejected | all
  const [editingDate, setEditingDate] = useState(null); // post.id while date-editor is open
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("alphalane_admin_token") : null;

  const fetchAll = async () => {
    if (!token) return navigate("/admin/login");
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/blog`, { headers: { "x-admin-token": token } });
      setPosts(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("alphalane_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Konnte Blog-Posts nicht laden.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const body = topic.trim() ? { topic: topic.trim(), broadcast: false } : { broadcast: false };
      const res = await axios.post(`${API}/admin/blog/generate`, body, { headers: { "x-admin-token": token } });
      const slug = res.data?.post?.slug;
      toast.success(`Blog generiert! ${slug ? "Slug: " + slug : ""}`);
      setTopic("");
      await fetchAll();
    } catch {
      toast.error("Generierung fehlgeschlagen — bitte erneut versuchen.");
    } finally {
      setGenerating(false);
    }
  };

  const [broadcastModal, setBroadcastModal] = useState(null); // {post, preview, selectedGroups}

  const openBroadcastModal = async (p) => {
    setBusy(p.id);
    try {
      const res = await axios.get(`${API}/admin/blog/${p.slug}/broadcast-preview`, { headers: { "x-admin-token": token } });
      setBroadcastModal({ post: p, preview: res.data, selectedGroups: ["A", "B", "C", "D"] });
    } catch {
      toast.error("Vorschau konnte nicht geladen werden.");
    } finally {
      setBusy(null);
    }
  };

  const confirmBroadcast = async () => {
    if (!broadcastModal) return;
    const { post: p, selectedGroups } = broadcastModal;
    if (!selectedGroups.length) {
      toast.error("Bitte mindestens eine Gruppe auswählen.");
      return;
    }
    setBusy(p.id);
    try {
      const res = await axios.post(
        `${API}/admin/blog/${p.slug}/broadcast`,
        { groups: selectedGroups },
        { headers: { "x-admin-token": token } },
      );
      const r = res.data?.broadcast || {};
      toast.success(`Broadcast versendet — ${r.sent || 0} neu, ${r.skipped || 0} bereits, ${r.errors || 0} Fehler.`);
      setBroadcastModal(null);
      await fetchAll();
    } catch {
      toast.error("Broadcast fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Blog "${p.title}" wirklich löschen? Diese Aktion ist nicht rückgängig zu machen.`)) return;
    setBusy(p.id);
    try {
      await axios.delete(`${API}/admin/blog/${p.slug}`, { headers: { "x-admin-token": token } });
      setPosts((xs) => xs.filter((x) => x.id !== p.id));
      toast.success("Blog gelöscht.");
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  const approve = async (p) => {
    setBusy(p.id);
    try {
      await axios.post(`${API}/admin/blog/${p.slug}/approve`, {}, { headers: { "x-admin-token": token } });
      setPosts((xs) => xs.map((x) => (x.id === p.id ? { ...x, status: "approved" } : x)));
      toast.success("Blog freigegeben — jetzt öffentlich.");
    } catch {
      toast.error("Freigabe fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  const reject = async (p) => {
    if (!window.confirm(`Blog "${p.title}" ablehnen? Wird nicht öffentlich angezeigt, bleibt aber im Archiv.`)) return;
    setBusy(p.id);
    try {
      await axios.post(`${API}/admin/blog/${p.slug}/reject`, {}, { headers: { "x-admin-token": token } });
      setPosts((xs) => xs.map((x) => (x.id === p.id ? { ...x, status: "rejected" } : x)));
      toast.success("Blog abgelehnt.");
    } catch {
      toast.error("Ablehnung fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  const unpublish = async (p) => {
    if (!window.confirm(`„${p.title}" zurück in „Wartet auf Freigabe" verschieben? Der Artikel wird sofort von der öffentlichen Seite entfernt.`)) return;
    setBusy(p.id);
    try {
      await axios.post(`${API}/admin/blog/${p.slug}/unpublish`, {}, { headers: { "x-admin-token": token } });
      setPosts((xs) => xs.map((x) => (x.id === p.id ? { ...x, status: "pending" } : x)));
      toast.success("Zurück in „Wartet auf Freigabe verschoben.");
    } catch {
      toast.error("Verschieben fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  // Production URL of the live deployment — used by the "An Live senden" button.
  // Detected as: the current host's preview prefix swapped for the configured prod domain.
  const LIVE_URL = "https://alphalane-tradingsystems.com";
  const isPreviewEnv = typeof window !== "undefined" && window.location.host.includes("preview.emergentagent.com");

  const pushToLive = async (p) => {
    if (!isPreviewEnv) {
      toast.error("Dieser Button ist nur aus der Preview-Umgebung nutzbar.");
      return;
    }
    if (!window.confirm(`„${p.title}" direkt an Live senden? Wird dort als „Wartet auf Freigabe" angelegt.`)) return;

    // Live's admin token can differ from preview's (different env var). Prompt once, cache in localStorage.
    let liveToken = localStorage.getItem("alphalane_live_admin_token");
    if (!liveToken) {
      liveToken = window.prompt(
        "Bitte einmalig das Admin-Passwort für die Live-Umgebung (alphalane-tradingsystems.com/admin/login) eingeben.\n\nWird verschlüsselt im Browser gespeichert.",
        ""
      );
      if (!liveToken) {
        toast.error("Senden abgebrochen — kein Live-Token eingegeben.");
        return;
      }
      localStorage.setItem("alphalane_live_admin_token", liveToken.trim());
    }

    setBusy(p.id);
    try {
      // Step 1: Slim export (no inline image bytes) — Live backend will fetch
      // the cover server-to-server from `source_image_url`. Keeps the cross-origin
      // POST body tiny (~5 KB) to avoid Cloudflare body-size limits / Network errors.
      const { data: exportData } = await axios.get(
        `${API}/admin/blog/${p.slug}/export?include_image_data=false`,
        { headers: { "x-admin-token": token } }
      );
      // Override source_image_url with our PUBLIC API base — backend's request.base_url
      // resolves to the internal Kubernetes hostname which Live can't reach.
      const coverPath = (exportData.post?.cover || "").split("?")[0];
      if (coverPath && coverPath.startsWith("/api/blog-image/")) {
        exportData.source_image_url = `${API}${coverPath}`;
      }
      // Step 2: POST slim payload to Live
      const { data: importResult } = await axios.post(
        `${LIVE_URL}/api/admin/blog/import`,
        exportData,
        { headers: { "x-admin-token": liveToken, "Content-Type": "application/json" }, timeout: 60000 }
      );
      toast.success(`An Live gesendet — Slug „${importResult.slug}" (wartet auf Freigabe).`);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message;
      if (status === 401) {
        // Clear cached token so user gets re-prompted next time
        localStorage.removeItem("alphalane_live_admin_token");
        toast.error("Falsches Live-Admin-Passwort — beim nächsten Versuch wirst du erneut gefragt.");
      } else {
        toast.error(`Senden an Live fehlgeschlagen: ${detail}`);
      }
    } finally {
      setBusy(null);
    }
  };

  const exportPost = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await axios.get(
        `${API}/admin/blog/${p.slug}/export`,
        { headers: { "x-admin-token": token } }
      );
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${p.slug}.alphalane.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export heruntergeladen.");
    } catch {
      toast.error("Export fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  const importPost = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const { data } = await axios.post(
        `${API}/admin/blog/import`,
        payload,
        { headers: { "x-admin-token": token, "Content-Type": "application/json" }, timeout: 60000 }
      );
      toast.success(`Importiert als „${data.slug}" (wartet auf Freigabe).`);
      // Reload blog list
      const fresh = await axios.get(`${API}/admin/blog`, { headers: { "x-admin-token": token } });
      setPosts(fresh.data);
      setFilter("pending");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import fehlgeschlagen — JSON ungültig?");
    }
  };

  const regenerateCover = async (p) => {
    if (!window.confirm(`Cover für „${p.title}" neu generieren? Das dauert ca. 10-15 Sekunden und verbraucht etwas LLM-Budget.`)) return;
    setBusy(p.id);
    try {
      const { data } = await axios.post(
        `${API}/admin/blog/${p.slug}/regenerate-cover`,
        {},
        { headers: { "x-admin-token": token }, timeout: 60000 }
      );
      setPosts((xs) => xs.map((x) => (x.id === p.id ? { ...x, cover: data.cover } : x)));
      toast.success("Neues Cover erstellt!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cover-Regenerierung fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  const updateDate = async (p, isoDate) => {
    if (!isoDate || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
      toast.error("Ungültiges Datum.");
      return;
    }
    setBusy(p.id);
    try {
      const { data } = await axios.patch(
        `${API}/admin/blog/${p.slug}`,
        { published_at: isoDate, date: isoDate },
        { headers: { "x-admin-token": token } }
      );
      setPosts((xs) => xs.map((x) => (x.id === p.id ? { ...x, published_at: data.published_at, date: data.date } : x)));
      toast.success("Datum aktualisiert.");
      setEditingDate(null);
    } catch {
      toast.error("Datum konnte nicht gespeichert werden.");
    } finally {
      setBusy(null);
    }
  };

  const filteredPosts = filter === "all" ? posts : posts.filter((p) => (p.status || "approved") === filter);
  const counts = {
    pending: posts.filter((p) => p.status === "pending").length,
    approved: posts.filter((p) => (p.status || "approved") === "approved").length,
    rejected: posts.filter((p) => p.status === "rejected").length,
    all: posts.length,
  };

  const logout = () => {
    localStorage.removeItem("alphalane_admin_token");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)]">
      <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Alphalane" className="h-9 w-9 object-contain" />
            <div className="font-display font-semibold text-[15px]">
              <span className="al-grad-text">ALPHALANE</span>
              <span className="text-slate-500 text-xs ml-2 font-medium uppercase tracking-widest">Admin</span>
            </div>
          </Link>
          <button onClick={logout} data-testid="admin-logout" className="al-cta-ghost !py-2 !px-3 text-sm">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      <AdminTopNav />

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <span className="al-tag">Content</span>
            <h1 className="mt-3 font-display font-bold text-3xl sm:text-4xl">
              Blog-<span className="al-grad-text">Verwaltung.</span>
            </h1>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">
              Auto-Generierung läuft <strong>alle 3 Wochen Sonntag 18:00 (Zürich)</strong>. Broadcast-Mails sind <strong>nicht automatisch</strong> —
              du wirst per WhatsApp informiert wenn ein neuer Blog ready ist, deployst, und triggerst dann die Broadcast-Mail manuell.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isPreviewEnv && (
              <button
                onClick={() => {
                  if (window.confirm('Gespeichertes Live-Admin-Passwort löschen? Wirst beim nächsten Push erneut gefragt.')) {
                    localStorage.removeItem("alphalane_live_admin_token");
                    toast.success("Live-Token zurückgesetzt.");
                  }
                }}
                data-testid="reset-live-token"
                title="Gespeichertes Live-Admin-Passwort löschen"
                className="al-cta-ghost !py-2 !px-3 text-sm"
              >
                <Rocket className="h-4 w-4" /> Live-Token zurücksetzen
              </button>
            )}
            <label className="al-cta-ghost !py-2 !px-3 text-sm cursor-pointer" data-testid="blog-import-btn">
              <Upload className="h-4 w-4" /> Import (.json)
              <input
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={(e) => importPost(e.target.files?.[0])}
              />
            </label>
            <button onClick={fetchAll} className="al-cta-ghost !py-2 !px-3 text-sm">
              <RefreshCw className="h-4 w-4" /> Neu laden
            </button>
          </div>
        </div>

        {/* Manual generation */}
        <div className="al-card p-6 mb-6" data-testid="blog-manual-gen">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-slate-500">
            <Sparkles className="h-3.5 w-3.5" /> Manuelle Generierung
          </div>
          <h3 className="mt-1 font-display font-semibold text-lg">Sofort einen neuen Blog erstellen</h3>
          <p className="mt-1 text-sm text-slate-600">Optional ein Thema vorgeben — sonst wählt Gemini automatisch aus 12 Trading/KI-Topics. Bilder werden parallel generiert. Broadcast wird <strong>nicht</strong> automatisch ausgelöst.</p>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <input
              data-testid="blog-topic-input"
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder='Optional: eigenes Thema, z.B. „Bitcoin-Halving 2026 und algorithmische Strategien"…'
              className="flex-1 al-input-sm"
              disabled={generating}
            />
            <button
              data-testid="blog-generate"
              onClick={generate}
              disabled={generating}
              className="al-cta !py-2.5 !px-5 text-sm whitespace-nowrap"
            >
              {generating ? <><Loader2 className="h-4 w-4 animate-spin" /> Generiere…</> : <><Sparkles className="h-4 w-4" /> Blog generieren</>}
            </button>
          </div>
        </div>

        {/* Workflow info card */}
        <div className="al-card p-5 mb-6 bg-amber-50/60 border-amber-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-[13px] leading-relaxed text-amber-900">
              <strong>Approval-Workflow:</strong> Auto-Generierte AI-Blogs landen jetzt zuerst in <em>„Wartet auf Freigabe"</em>. Du gibst sie hier per Klick frei, dann werden sie öffentlich. <strong>Broadcast-Mails</strong> erst nach Deploy auslösen — sonst gehen E-Mails mit 404-Links raus!
            </div>
          </div>
        </div>

        {/* Status filter tabs */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="blog-filter-tabs">
          {[
            { id: "pending", label: "Wartet auf Freigabe", color: "amber" },
            { id: "approved", label: "Live", color: "emerald" },
            { id: "rejected", label: "Abgelehnt", color: "rose" },
            { id: "all", label: "Alle", color: "slate" },
          ].map((t) => {
            const active = filter === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setFilter(t.id)}
                data-testid={`blog-filter-${t.id}`}
                className={`px-4 py-2 rounded-full text-sm font-display font-semibold transition-all ${
                  active
                    ? "al-grad-bg text-white shadow-md"
                    : "bg-white border border-[var(--al-line)] text-slate-600 hover:border-[var(--al-purple)]/30"
                }`}
              >
                {t.label}
                <span className={`ml-2 text-[11px] font-mono-tab ${active ? "text-white/80" : "text-slate-400"}`}>
                  {counts[t.id]}
                </span>
              </button>
            );
          })}
        </div>

        {/* List */}
        {loading ? (
          <div className="al-card p-12 text-center text-slate-500">Lade Blog-Posts…</div>
        ) : filteredPosts.length === 0 ? (
          <div className="al-card p-12 text-center text-slate-500">Keine Blog-Posts in dieser Kategorie.</div>
        ) : (
          <div className="al-card overflow-hidden" data-testid="blog-list">
            <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-[var(--al-line)] bg-[var(--al-bg)] text-[11px] uppercase tracking-widest font-semibold text-slate-500">
              <div className="col-span-5">Titel</div>
              <div className="col-span-2">Status / Veröffentlicht</div>
              <div className="col-span-2 text-right">Broadcast</div>
              <div className="col-span-3 text-right">Aktion</div>
            </div>
            {filteredPosts.map((p) => {
              const isPending = p.status === "pending";
              const isRejected = p.status === "rejected";
              return (
                <div key={p.id} data-testid={`blog-row-${p.id}`} className="grid grid-cols-12 gap-3 px-5 py-3.5 items-center border-b border-[var(--al-line)] last:border-b-0 hover:bg-slate-50 transition-colors">
                  <div className="col-span-5 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <SourceBadge source={p.source} />
                      {isPending || isRejected ? (
                        <span className="font-display font-semibold text-slate-900 truncate text-sm">
                          {p.title}
                        </span>
                      ) : (
                        <Link to={`/blog/${p.slug}`} target="_blank" className="font-display font-semibold text-slate-900 hover:text-[var(--al-purple)] truncate text-sm">
                          {p.title}
                        </Link>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-400 truncate mt-0.5 font-mono-tab">/{p.slug}</div>
                  </div>
                  <div className="col-span-2 flex flex-col gap-1" data-testid={`blog-date-cell-${p.id}`}>
                    <StatusBadge status={p.status} />
                    {editingDate === p.id ? (
                      <DateEditor
                        initialIso={p.published_at || p.date}
                        onSave={(iso) => updateDate(p, iso)}
                        onCancel={() => setEditingDate(null)}
                        busy={busy === p.id}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEditingDate(p.id)}
                        data-testid={`blog-date-edit-${p.id}`}
                        title="Veröffentlichungsdatum ändern"
                        className="text-xs text-slate-500 hover:text-[var(--al-purple)] inline-flex items-center gap-1 group text-left"
                      >
                        <Calendar className="h-3 w-3 opacity-60 group-hover:opacity-100" />
                        {p.status === "approved" && p.published_at
                          ? `Live seit ${formatDate(p.published_at)}`
                          : p.status === "pending"
                          ? "noch nicht veröffentlicht"
                          : p.status === "rejected"
                          ? "—"
                          : formatDate(p.date)}
                        <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-60" />
                      </button>
                    )}
                  </div>
                  <div className="col-span-2 text-right">
                    {p.broadcast_count > 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-display font-semibold">
                        <CheckCircle2 className="h-3.5 w-3.5" /> {p.broadcast_count} Mails
                      </span>
                    ) : (
                      <span className="text-[11px] text-slate-400">—</span>
                    )}
                  </div>
                  <div className="col-span-3 flex justify-end gap-1.5 flex-wrap">
                    {isPending && (
                      <>
                        <button
                          onClick={() => approve(p)}
                          disabled={busy === p.id}
                          data-testid={`blog-approve-${p.id}`}
                          title="Freigeben — wird öffentlich angezeigt"
                          className="px-3 py-1.5 rounded-md text-xs font-display font-semibold bg-emerald-500 hover:bg-emerald-600 text-white inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                        >
                          {busy === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                          Freigeben
                        </button>
                        <button
                          onClick={() => reject(p)}
                          disabled={busy === p.id}
                          data-testid={`blog-reject-${p.id}`}
                          title="Ablehnen"
                          className="px-3 py-1.5 rounded-md text-xs font-display font-semibold border border-slate-200 hover:border-rose-300 hover:bg-rose-50 text-slate-700 hover:text-rose-700 inline-flex items-center gap-1.5 transition-all disabled:opacity-50"
                        >
                          <X className="h-3.5 w-3.5" />
                          Ablehnen
                        </button>
                      </>
                    )}
                    {isRejected && (
                      <button
                        onClick={() => approve(p)}
                        disabled={busy === p.id}
                        data-testid={`blog-restore-${p.id}`}
                        title="Wieder freigeben"
                        className="px-3 py-1.5 rounded-md text-xs font-display font-semibold border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 text-slate-700 hover:text-emerald-700 inline-flex items-center gap-1.5 transition-all disabled:opacity-50"
                      >
                        <Check className="h-3.5 w-3.5" />
                        Wieder freigeben
                      </button>
                    )}
                    {!isPending && !isRejected && (
                      <button
                        onClick={() => openBroadcastModal(p)}
                        disabled={busy === p.id}
                        data-testid={`blog-broadcast-${p.id}`}
                        title="Broadcast-Mail an alle Subscriber"
                        className="px-3 py-1.5 rounded-md text-xs font-display font-semibold al-grad-bg text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5 transition-opacity"
                      >
                        {busy === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                        Broadcast
                      </button>
                    )}
                    {!isPending && !isRejected && (
                      <button
                        onClick={() => unpublish(p)}
                        disabled={busy === p.id}
                        data-testid={`blog-unpublish-${p.id}`}
                        title="Zurück nach Wartet auf Freigabe"
                        className="px-2.5 py-1.5 rounded-md text-xs font-display font-semibold border border-slate-200 hover:border-amber-300 hover:bg-amber-50 text-slate-700 hover:text-amber-700 inline-flex items-center gap-1.5 transition-all disabled:opacity-50"
                      >
                        <Archive className="h-3.5 w-3.5" />
                        Zurückziehen
                      </button>
                    )}
                    {p.cover && p.cover.includes("/api/blog-image/") && (
                      <button
                        onClick={() => regenerateCover(p)}
                        disabled={busy === p.id}
                        data-testid={`blog-regen-cover-${p.id}`}
                        title="Cover-Bild neu generieren (Nano Banana)"
                        className="p-2 rounded-md hover:bg-violet-50 text-violet-600 transition-colors disabled:opacity-50"
                      >
                        {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                      </button>
                    )}
                    {!isPending && !isRejected && (
                      <Link
                        to={`/blog/${p.slug}`}
                        target="_blank"
                        title="Im neuen Tab öffnen"
                        className="p-2 rounded-md hover:bg-slate-100 text-slate-500 transition-colors"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Link>
                    )}
                    <button
                      onClick={() => exportPost(p)}
                      disabled={busy === p.id}
                      data-testid={`blog-export-${p.id}`}
                      title="Als .json herunterladen (für Import in andere Umgebung)"
                      className="p-2 rounded-md hover:bg-slate-100 text-slate-500 transition-colors disabled:opacity-50"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                    {isPreviewEnv && (
                      <button
                        onClick={() => pushToLive(p)}
                        disabled={busy === p.id}
                        data-testid={`blog-push-live-${p.id}`}
                        title="Direkt an Live-Umgebung senden (wartet auf Freigabe)"
                        className="px-3 py-1.5 rounded-md text-xs font-display font-semibold bg-violet-600 hover:bg-violet-700 text-white inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                      >
                        {busy === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rocket className="h-3.5 w-3.5" />}
                        An Live senden
                      </button>
                    )}
                    <button
                      onClick={() => remove(p)}
                      disabled={busy === p.id}
                      data-testid={`blog-delete-${p.id}`}
                      title="Löschen"
                      className="p-2 rounded-md hover:bg-rose-50 text-rose-500 transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <style>{`
        .al-input-sm { width: 100%; padding: 9px 12px; border-radius: 10px; border: 1px solid var(--al-line); background: white; font-family: 'Manrope', sans-serif; font-size: 14px; color: var(--al-ink); transition: border-color 200ms, box-shadow 200ms; }
        .al-input-sm:focus { outline: none; border-color: var(--al-purple); box-shadow: 0 0 0 3px rgba(139,92,246,0.10); }
        .al-input-sm:disabled { background: #FBFAFF; color: #94A3B8; }
      `}</style>

      {broadcastModal && (
        <BroadcastModal
          modal={broadcastModal}
          setSelected={(sel) => setBroadcastModal({ ...broadcastModal, selectedGroups: sel })}
          onConfirm={confirmBroadcast}
          onCancel={() => setBroadcastModal(null)}
          busy={busy === broadcastModal.post.id}
        />
      )}
    </div>
  );
}

const GROUP_INFO = {
  A: { label: "Insider-Leads", color: "violet", note: "Insider-Report angefordert" },
  B: { label: "Neu / unbearbeitet", color: "amber", note: "Kontaktformular ausgefüllt" },
  C: { label: "Kontaktiert", color: "emerald", note: "Persönlich angesprochen" },
  D: { label: "Kunden", color: "purple", note: "Aktive Alphalane-Kunden" },
};

function BroadcastModal({ modal, setSelected, onConfirm, onCancel, busy }) {
  const { post, preview, selectedGroups } = modal;
  const counts = preview?.groups || {};
  const selectedTotal = selectedGroups.reduce((sum, g) => sum + (counts[g] || 0), 0);

  const toggle = (g) => {
    if (selectedGroups.includes(g)) setSelected(selectedGroups.filter((x) => x !== g));
    else setSelected([...selectedGroups, g]);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      data-testid="broadcast-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="al-card w-full max-w-lg p-6 bg-white max-h-[90vh] overflow-y-auto">
        <h3 className="font-display font-bold text-xl">Broadcast versenden</h3>
        <p className="mt-1 text-sm text-slate-600 line-clamp-2">{post.title}</p>

        <div className="mt-5">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3">
            Empfänger-Gruppen auswählen
          </div>
          <div className="space-y-2">
            {["A", "B", "C", "D"].map((g) => {
              const info = GROUP_INFO[g];
              const active = selectedGroups.includes(g);
              const count = counts[g] || 0;
              return (
                <label
                  key={g}
                  data-testid={`broadcast-group-${g}`}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                    active ? "border-[var(--al-purple)] bg-violet-50/40" : "border-slate-200 hover:border-slate-300"
                  } ${count === 0 ? "opacity-50" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() => toggle(g)}
                    disabled={count === 0}
                    className="h-4 w-4 accent-[var(--al-purple)]"
                  />
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-violet-100 text-violet-800 text-xs font-bold">
                    {g}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-sm">{info.label}</div>
                    <div className="text-xs text-slate-500">{info.note}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-tab font-bold text-lg tabular-nums">{count}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">Empfänger</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
          <div className="text-xs text-amber-900 leading-relaxed">
            <strong>Total:</strong> {selectedTotal} Empfänger werden den Blog-Beitrag erhalten.
            Bereits versandt: {post.broadcast_count || 0}. Wer den Post schon hat oder Drip abbestellt hat, wird übersprungen.
          </div>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold hover:bg-slate-50"
            data-testid="broadcast-cancel"
          >
            Abbrechen
          </button>
          <button
            onClick={onConfirm}
            disabled={busy || selectedTotal === 0}
            className="px-5 py-2 rounded-full al-grad-bg text-white text-sm font-semibold disabled:opacity-50"
            data-testid="broadcast-confirm"
          >
            {busy ? "Sende…" : `An ${selectedTotal} senden`}
          </button>
        </div>
      </div>
    </div>
  );
}

function DateEditor({ initialIso, onSave, onCancel, busy }) {
  // Convert ISO datetime or YYYY-MM-DD into YYYY-MM-DD for the <input type="date">
  const initialDay = (initialIso || "").slice(0, 10) || new Date().toISOString().slice(0, 10);
  const [val, setVal] = useState(initialDay);
  return (
    <div className="flex items-center gap-1 mt-1" data-testid="date-editor">
      <input
        type="date"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        disabled={busy}
        data-testid="date-editor-input"
        className="text-xs px-2 py-1 rounded border border-slate-300 focus:outline-none focus:border-[var(--al-purple)] focus:ring-2 focus:ring-[var(--al-purple)]/20 font-mono-tab"
        autoFocus
      />
      <button
        onClick={() => onSave(val)}
        disabled={busy}
        data-testid="date-editor-save"
        title="Speichern"
        className="p-1 rounded text-emerald-600 hover:bg-emerald-50 disabled:opacity-40"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
      </button>
      <button
        onClick={onCancel}
        disabled={busy}
        data-testid="date-editor-cancel"
        title="Abbrechen"
        className="p-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-40"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
